/**
 * Local speech recognition only (docs/ux/voice-call.md "Local speech on the web"; architecture
 * §3). Audio never goes to a browser vendor's cloud recognizer:
 *
 * 1. **Browser on-device:** the Web Speech API with `processLocally = true` (Chrome 139+), used
 *    only when `SpeechRecognition.available({ processLocally: true })` answers `available`. It
 *    fails closed: an on-device error ends the turn and is never retried without the flag.
 * 2. **Your Mac:** otherwise the audio is sealed and sent to your own Mac, where `viko`
 *    transcribes it with whisper (lib/call/mac-whisper.ts).
 * 3. **Neither:** no voice input (type instead). Safari's `webkitSpeechRecognition` has no
 *    on-device guarantee, so it is never used.
 *
 * Shared by Viko Call, dictation (lib/speech.ts) and voice mode (lib/voice/useVoice.ts).
 */

export interface RecognitionResultList {
  resultIndex: number
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>
}

export interface LocalRecognition {
  continuous: boolean
  interimResults: boolean
  lang: string
  processLocally?: boolean
  start(): void
  stop(): void
  abort(): void
  onresult: ((e: RecognitionResultList) => void) | null
  onend: (() => void) | null
  onerror: ((e: { error: string }) => void) | null
  onspeechstart?: (() => void) | null
}

export type Availability = 'available' | 'downloadable' | 'downloading' | 'unavailable'

export interface RecognitionClass {
  new (): LocalRecognition
  prototype: object
  available?(o: { langs: string[]; processLocally: boolean }): Promise<Availability>
  install?(o: { langs: string[]; processLocally: boolean }): Promise<boolean>
}

/** The constructor, only if it can run on-device (has `processLocally` and `available`). */
export function localRecognitionClass(g: unknown = globalThis): RecognitionClass | undefined {
  const w = g as {
    SpeechRecognition?: RecognitionClass
    webkitSpeechRecognition?: RecognitionClass
  }
  for (const C of [w.SpeechRecognition, w.webkitSpeechRecognition]) {
    if (!C) continue
    if (!('processLocally' in C.prototype)) continue
    if (typeof C.available !== 'function') continue
    return C
  }
  return undefined
}

export type SpeechPath = 'browser-local' | 'mac-whisper' | 'none'

export interface SpeechPathChoice {
  path: SpeechPath
  /** Shown in the mode chip: "Chrome on-device", "whisper on Mac Studio", "type instead". */
  label: string
  /** The on-device pack can be installed (Chrome `downloadable`). */
  installable?: boolean
}

export function speechLang(g: unknown = globalThis): string {
  return (g as { navigator?: { language?: string } }).navigator?.language || 'en-US'
}

/** Pick the local path for this browser and machine. Never a cloud recognizer. */
export async function chooseSpeechPath(o: {
  g?: unknown
  /** The Mac that would transcribe (online, encrypted, transcription available). */
  mac?: { name: string } | undefined
  lang?: string
}): Promise<SpeechPathChoice> {
  const C = localRecognitionClass(o.g)
  const lang = o.lang ?? speechLang(o.g)
  let state: Availability | undefined
  if (C?.available) {
    try {
      state = await C.available({ langs: [lang], processLocally: true })
    } catch {
      state = undefined
    }
  }
  if (state === 'available') return { path: 'browser-local', label: 'on-device speech' }
  if (o.mac) return { path: 'mac-whisper', label: `whisper on ${o.mac.name}` }
  return {
    path: 'none',
    label: 'type instead',
    ...(state === 'downloadable' ? { installable: true } : {}),
  }
}

/**
 * The likely path without probing (labels before a call starts). The real choice is made with
 * `chooseSpeechPath` when the call or the mic starts.
 */
export function guessSpeechPath(
  mac: { name: string } | undefined,
  g: unknown = globalThis,
): SpeechPathChoice {
  if (localRecognitionClass(g)) return { path: 'browser-local', label: 'on-device speech' }
  if (mac) return { path: 'mac-whisper', label: `whisper on ${mac.name}` }
  return { path: 'none', label: 'type instead' }
}

export class LocalSpeechRefused extends Error {
  constructor(readonly reason: 'not-local' | 'not-available' | string) {
    super(
      reason === 'not-local'
        ? 'This browser cannot guarantee on-device recognition.'
        : `On-device speech stopped (${reason}).`,
    )
  }
}

/** Errors that mean the on-device recognizer can't serve this turn: end it, never fall back. */
const FAIL_CLOSED = new Set([
  'service-not-allowed',
  'language-not-supported',
  'network',
  'not-allowed',
  'audio-capture',
])

export interface LocalRecognitionHandlers {
  onInterim?(text: string): void
  onFinal(text: string): void
  onSpeechStart?(): void
  onEnd?(): void
  onError?(e: LocalSpeechRefused): void
  continuous?: boolean
}

/**
 * Start on-device recognition, or throw. The flag is set before `start()` and read back: an
 * implementation that ignores it (no on-device support) is refused rather than started.
 */
export function startLocalRecognition(
  C: RecognitionClass,
  lang: string,
  h: LocalRecognitionHandlers,
): LocalRecognition {
  const r = new C()
  r.processLocally = true
  if (r.processLocally !== true) throw new LocalSpeechRefused('not-local')
  r.continuous = h.continuous ?? true
  r.interimResults = true
  r.lang = lang
  r.onresult = (e) => {
    let live = ''
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const res = e.results[i]
      if (!res) continue
      if (res.isFinal) h.onFinal(res[0].transcript.trim())
      else live += res[0].transcript
    }
    h.onInterim?.(live)
  }
  r.onspeechstart = () => h.onSpeechStart?.()
  r.onerror = (e) => {
    // Fail closed: report and end. Nothing here ever restarts without `processLocally`.
    if (FAIL_CLOSED.has(e.error) || e.error) h.onError?.(new LocalSpeechRefused(e.error))
  }
  r.onend = () => h.onEnd?.()
  r.start()
  return r
}
